import { Alert, AlertTitle, Box, Stack, Typography } from "@mui/material";
import { useState } from "react";
import type { ApiError } from "@entrilia/shared/api/types";
import FieldsDiffTable from "@entrilia/shared/components/FieldsDiffTable";
import { logError } from "@entrilia/shared/logging";
import type { ChangeRequestDetails } from "../../../../api/types/changeRequestsTypes";
import ChangesWarningAlert from "../common/ChangesWarningAlert";
import PendingChangeRequestDialog from "../common/PendingChangeRequestDialog";
import PendingChangesAlert from "../common/PendingChangesAlert";

interface Props {
  investorId: string;
  investorName: string;
  canUserEdit: boolean;
  pendingChangeRequest?: ChangeRequestDetails;
  fetchError?: ApiError;
  onChangeRequestCanceled: () => void;
}

const ChangeRequestStatus = ({
  investorId,
  investorName,
  canUserEdit,
  pendingChangeRequest,
  fetchError,
  onChangeRequestCanceled,
}: Props) => {
  const [detailsOpen, setDetailsOpen] = useState(false);

  if (fetchError) {
    logError(fetchError, "[ChangeRequestStatus] fetchError");
    return (
      <Alert severity="error">
        <AlertTitle>Failed to load change request status for this investor</AlertTitle>
      </Alert>
    );
  }

  if (!pendingChangeRequest) {
    return canUserEdit ? <ChangesWarningAlert /> : null;
  }

  const handleChangeRequestCanceled = () => {
    setDetailsOpen(false);
    onChangeRequestCanceled();
  };

  return (
    <>
      <PendingChangesAlert
        pendingChangeRequest={pendingChangeRequest}
        onViewRequest={() => setDetailsOpen(true)}
        showEditingRestrictedNote={canUserEdit}
      />
      <PendingChangeRequestDialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        canUserEdit={canUserEdit}
        investorId={investorId}
        changeRequestDetails={pendingChangeRequest}
        onChangeRequestCanceled={handleChangeRequestCanceled}
      >
        <Stack spacing={0.5}>
          <Typography variant="subtitle1">Investor Details</Typography>
          {investorName && <Typography color="textSecondary">{investorName}</Typography>}
        </Stack>
        <Box sx={{ height: "100%", pb: 2 }}>
          <FieldsDiffTable
            fieldChanges={pendingChangeRequest.fieldChanges ?? []}
            oldValueColumnLabel="Current"
            newValueColumnLabel="Change"
            sortKeys
          />
        </Box>
        {pendingChangeRequest.comment && (
          <Stack spacing={0.5}>
            <Typography variant="subtitle1">Comment</Typography>
            <Typography color="textSecondary" sx={{ whiteSpace: "break-spaces" }}>
              {pendingChangeRequest.comment}
            </Typography>
          </Stack>
        )}
      </PendingChangeRequestDialog>
    </>
  );
};

export default ChangeRequestStatus;
